import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { identityCardActions } from './identity_card.actions';
import { environment } from '../../../../environments/environment';
import { IdentityCardExtraction, UploadedIdentityCard, ViewedIdentityCard } from '../../../models/document.model';

@Injectable()
export class IdentityCardEffects {
    private httpClient = inject(HttpClient);
    private actions$ = inject(Actions);
    private store = inject(Store);

    getPresignedUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.getPresignedUrl),
            exhaustMap(({ filename }) => {
                let params = new HttpParams().set('filename', filename);
                return this.httpClient.get<string>(`${environment.api}/document/identity_card/insertion_url`, { params }).pipe(
                    map((payload) => identityCardActions.getPresignedUrlSuccess({ payload })),
                    catchError((error) => {
                        this.store.dispatch(identityCardActions.getPresignedUrlFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    uploadS3$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.uploadS3),
            exhaustMap(({ data, url }) => {
                let headers = new HttpHeaders();
                headers = headers.set('Content-Type', data.type || 'application/octet-stream');
                return this.httpClient.put(url, data, { headers }).pipe(
                    map(() => identityCardActions.uploadS3Success()),
                    catchError((error) => {
                        this.store.dispatch(identityCardActions.uploadS3Fail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    uploadBackend$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.uploadBackend),
            exhaustMap(({ data, file, customer_id, file_type, timestamp }) => {
                let req;
                if (file_type === 'pdf') {
                    const formData = new FormData();
                    formData.append('file', file!);
                    const params = new HttpParams().set('customer_id', customer_id!.toString());
                    req = this.httpClient.post<UploadedIdentityCard>(
                        `${environment.api}/document/identity_card/`,
                        formData,
                        { params },
                    );
                } else {
                    const params = new HttpParams().set('customer_id', data!.customer_id.toString());
                    req = this.httpClient.post<UploadedIdentityCard>(
                        `${environment.api}/document/identity_card/image/path`,
                        { file_path: `identity_card/${timestamp}/` },
                        { params },
                    );
                }
                return req.pipe(
                    map((payload) => identityCardActions.uploadBackendSuccess({
                        payload: this.normalizeUploadedIdentityCard(payload),
                    })),
                    catchError((error) => {
                        this.store.dispatch(identityCardActions.uploadBackendFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    extract$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.extract),
            exhaustMap(({ identity_card_id }) => {
                const params = new HttpParams().set('identity_card_id', identity_card_id.toString());
                return this.httpClient.post(`${environment.api}/document/identity_card/extract_ws`, null, { params }).pipe(
                    map(() => identityCardActions.extractSuccess()),
                    catchError((error) => {
                        this.store.dispatch(identityCardActions.extractFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );


    editExtraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.editExtraction),
            exhaustMap(({ extracted_id, data }) => {
                return this.httpClient
                    .put(`${environment.api}/document/identity_card/modify_extraction/${extracted_id}`, data)
                    .pipe(map(() => identityCardActions.editExtractionSuccess({ payload: data })));
            })
        )
    );

    loadExtractionInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.loadExtractionInfo),
            exhaustMap(({ extracted_id }) => {
                return this.httpClient
                    .get<any>(`${environment.api}/document/identity_card/extraction_info/${extracted_id}`)
                    .pipe(
                        map((payload) => {
                            // Il backend restituisce IdentityCardExtraction piatta, non ViewedIdentityCard.
                            // extracted_identity_card_id è garantito dal path param se il backend non lo include nel body.
                            const wrapped: ViewedIdentityCard = {
                                identity_card_id: payload.identity_card_id ?? 0,
                                customer_id: payload.customer_id ?? 0,
                                status: 'Estratta',
                                extracted_card: {
                                    ...(payload as IdentityCardExtraction),
                                    extracted_identity_card_id: payload.extracted_identity_card_id ?? extracted_id,
                                },
                            };
                            return identityCardActions.loadExtractionInfoSuccess({ payload: wrapped });
                        }),
                        catchError((error) => {
                            this.store.dispatch(identityCardActions.loadExtractionInfoFail());
                            return throwError(() => new Error(error));
                        })
                    );
            })
        )
    );

    download$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.download),
            exhaustMap(({ ids }) => {
                return this.httpClient.post<string[]>(`${environment.api}/document/identity_card/download_links`, ids).pipe(
                    map((payload) => identityCardActions.downloadSuccess({ payload }))
                );
            })
        )
    );

    deleteDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.deleteDocument),
            exhaustMap(({ id }) => {
                return this.httpClient.delete(`${environment.api}/document/identity_card/${id}`).pipe(
                    map(() => identityCardActions.deleteDocumentSuccess()),
                    catchError((error) => {
                        this.store.dispatch(identityCardActions.deleteDocumentFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    deleteExtraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.deleteExtraction),
            exhaustMap(({ extracted_id }) => {
                return this.httpClient.delete(`${environment.api}/document/identity_card/extraction/${extracted_id}`).pipe(
                    map(() => identityCardActions.deleteExtractionSuccess()),
                    catchError((error) => {
                        this.store.dispatch(identityCardActions.deleteExtractionFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    getCSV$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.getCSV),
            exhaustMap(({ ids }) => {
                let params = new HttpParams();
                ids.forEach(id => params = params.append('ids', id.toString()));
                return this.httpClient.get(`${environment.api}/document/identity_card/csv`, { params, responseType: 'text' }).pipe(
                    map((payload) => identityCardActions.getCSVSuccess({ payload }))
                );
            })
        )
    );

    getExcel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(identityCardActions.getExcel),
            exhaustMap(({ ids }) => {
                let params = new HttpParams();
                ids.forEach(id => params = params.append('ids', id.toString()));
                return this.httpClient.get(`${environment.api}/document/identity_card/excel`, { params, responseType: 'blob' }).pipe(
                    map((payload) => identityCardActions.getExcelSuccess({ payload }))
                );
            })
        )
    );

    private normalizeUploadedIdentityCard(payload: unknown): UploadedIdentityCard {
        if (typeof payload === 'number') {
            return { identity_card_id: payload };
        }
        const p = payload as Record<string, unknown>;
        const id = p['identity_card_id'] ?? p['id'];
        return { identity_card_id: id as number };
    }
}
